import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useAuth } from '@/hooks/useAuth'
import { broadcastDeviceService } from '@/services/broadcast_device_service'
import { BroadcastDeviceVod } from '@/hooks/useBroadcastDeviceVodList'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import { BroadcastDeviceVodType } from '@/data/broadcastDeviceVodType'

interface VodDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  broadcastDeviceId?: number
  vod: BroadcastDeviceVod | null
  broadcastType: BroadcastDeviceVodType
  onSuccess?: () => void
}

const formSchema = z.object({
  vod_name: z.string().min(1, '請輸入影片名稱').max(100, '影片名稱不能超過100個字符'),
  source: z.string().min(1, '請輸入影片來源').max(200, '影片來源不能超過200個字符'),
  order: z.coerce.number().int().optional(),
  is_active: z.boolean().default(true),
})

type FormValues = z.infer<typeof formSchema>

export function VodDialog({
  open,
  onOpenChange,
  broadcastDeviceId,
  vod,
  broadcastType,
  onSuccess,
}: VodDialogProps) {
  const { token } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditing = !!vod

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vod_name: vod?.vod_name || '',
      source: vod?.source || '',
      order: vod?.order || undefined,
      is_active: vod?.is_active ?? true,
    },
  })

  // 監聽對話框開關狀態，關閉時重置表單
  useEffect(() => {
    if (!open) {
      form.reset({
        vod_name: '',
        source: '',
        order: undefined,
        is_active: true,
      })
    } else if (vod) {
      // 如果是編輯模式，填充表單數據
      form.reset({
        vod_name: vod.vod_name || '',
        source: vod.source || '',
        order: vod.order || undefined,
        is_active: vod.is_active ?? true,
      })
    }
  }, [open, vod, form])

  const onSubmit = async (values: FormValues) => {
    if (!token) return

    setIsSubmitting(true)

    try {
      let result

      if (isEditing && vod) {
        // Update existing VOD
        result = await broadcastDeviceService.updateBroadcastDeviceVod(
          {
            vod_id: vod.vod_id,
            vod_name: values.vod_name,
            source: values.source,
            order: values.order,
            is_active: values.is_active,
          },
          token,
        )
      } else if (broadcastDeviceId) {
        // Create new VOD
        result = await broadcastDeviceService.createBroadcastDeviceVod(
          broadcastDeviceId,
          {
            vod_name: values.vod_name,
            source: values.source,
            broadcast_type: broadcastType,
            order: values.order,
            is_active: values.is_active,
          },
          token,
        )
      } else {
        // Handle error - no broadcast device ID
        toast.error('缺少廣播設備ID')
        return
      }

      if (result.success) {
        toast.success(
          isEditing ? `已成功更新影片 ${values.vod_name}` : `已成功新增影片 ${values.vod_name}`,
        )
        onOpenChange(false)
        onSuccess?.()
      } else {
        toast.error(result.message || '請稍後再試')
      }
    } catch {
      toast.error('發生錯誤，請稍後再試')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? '編輯影片' : '新增影片'}</DialogTitle>
          <DialogDescription>
            {isEditing ? '修改影片資訊' : '新增影片到診間播放列表'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="vod_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>影片名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入影片名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>影片來源</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入影片來源網址" {...field} />
                  </FormControl>
                  <FormDescription>請輸入影片的來源網址，例如 YouTube 連結</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>排序</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="請輸入排序數字"
                      {...field}
                      value={field.value === undefined ? '' : field.value}
                    />
                  </FormControl>
                  <FormDescription>數字越小，排序越前面</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>啟用狀態</FormLabel>
                    <FormDescription>啟用後，此影片將會在診間播放</FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditing ? '更新中...' : '新增中...'}
                  </>
                ) : isEditing ? (
                  '更新'
                ) : (
                  '新增'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
